import { useAuth } from "@clerk/clerk-react";
import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { AlertContext } from "contexts/Alert";
import { CampaignsContext } from "contexts/Campaigns";
import { DAYS_OF_THE_WEEK, NewCampaignContext } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SubscriptionContext } from "contexts/Subscription";
import moment, { Moment } from "moment";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Campaign, CampaignStatus } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import Markdown from "components/Markdown";
import TemplateEditButton from "components/TemplateEditButton";
import OutreachSelect from "components/TemplateScreen/OutreachSelect";
import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { ON_BEHALF_OF_TOKEN } from "utils/localStorage";
import { convertTo12HourFormat } from "utils/time";
import { trackEvent } from "utils/tracking";

type TemplateError = {
  idx: number;
  message: string;
};

interface Props {
  campaign?: Campaign | null;
  totalSelectedBrands?: number;
  isLocal?: boolean;
}

export default function CampaignDetails({
  campaign,
  totalSelectedBrands = 0,
  isLocal = false,
}: Props) {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const {
    setTemplates,
    templates,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    selectedDays,
    setSelectedDays,
    timezone,
    setTimezone,
    selectedCollection,
    previewNewCampaign,
    setPreviewBrands,
    setPreviewPage,
    setPreviewCount,
    status,
    setStatus,
    excludedBentoBrandIds,
  } = useContext(NewCampaignContext);
  const { setCampaigns } = useContext(CampaignsContext);
  const isAdmin = Boolean(sessionStorage.getItem(ON_BEHALF_OF_TOKEN));
  const navigate = useNavigate();
  const { emailSettings, setEmailSettings } = useContext(
    OrganizationUserContext,
  );
  const { setUpgradeDialogSource } = useContext(SubscriptionContext);

  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    fetchOutreachTemplates,
  } = useContext(OutreachTemplatesContext);

  const [nextLoading, setNextLoading] = useState<boolean>(false);
  const [dateError, setDateError] = useState<string>("");
  const [dateLimitError, setDateLimitError] = useState<string>("");
  const [timeError, setTimeError] = useState<string>("");
  const [selectedDaysError, setSelectedDaysError] = useState<string>("");
  const [templatesError, setTemplatesError] = useState<TemplateError | null>(
    null,
  );
  const [recommendedEndTime, setRecommendedEndTime] = useState<string>("");
  const [displayEndDate, setDisplayedEndDate] = useState(false);

  const selectedFollowups = templates?.slice(1, templates?.length);
  const templateFromEmailSettings = emailSettings?.emailSettingTemplates?.map(
    (template) => template.templateId,
  );
  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );

  const addFollowUp = () => {
    setTemplates([...templates, followupTemplates?.[0]]);
  };

  const initializeEmails = () => {
    if (templates?.length > 0) return;

    if (allTemplates?.length === 0) {
      fetchOutreachTemplates();
      return;
    }

    const presetTemplates = emailSettings?.emailSettingTemplates || [];
    const matchedTemplates = [];

    for (const settingTemplate of presetTemplates) {
      const template = allTemplates?.find(
        (x) => x.id === settingTemplate.templateId,
      );
      if (template) {
        matchedTemplates.push(template);
      }
    }
    if (matchedTemplates?.length > 0) {
      setTemplates(matchedTemplates);
      return;
    }

    if (filteredOutreachTemplates?.length > 0) {
      setTemplates([filteredOutreachTemplates[0]]);
    } else if (filteredOutreachTemplates?.length === 0) {
      fetchOutreachTemplates();
    }
  };

  useEffect(() => {
    if (campaign?.id) {
      const startMoment = moment
        .tz(campaign.startDate, campaign?.timezone)
        .add(1, "days");
      setStartDate(startMoment);
      const endMoment = moment
        .tz(campaign.endDate, campaign?.timezone)
        .add(1, "days");
      setEndDate(endMoment);
      const startTime = convertTo12HourFormat(campaign?.startTime);
      const endTime = convertTo12HourFormat(campaign?.endTime);
      setStartTime(moment(startTime, "hh:mm A"));
      setEndTime(moment(endTime, "hh:mm A"));
      setSelectedDays(campaign?.daysOfWeek);
      setTimezone(campaign?.timezone);
      const templates: Template[] =
        campaign?.emailSetting?.emailSettingTemplates
          ?.map((emailSettingTemplate) =>
            allTemplates.find((t) => t.id === emailSettingTemplate.templateId),
          )
          .filter((template): template is Template => template !== undefined) ||
        [];
      setTemplates(templates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.id]);

  useEffect(() => {
    if (
      !campaign?.id &&
      templates.length === 0 &&
      emailSettings?.emailSettingTemplates
    ) {
      initializeEmails();
      if (emailSettings.timezone) {
        setTimezone(emailSettings.timezone);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSettings?.emailSettingTemplates?.length, allTemplates?.length]);

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const copy = [...templates];
    const template = allTemplates?.find((x) => x.id === value);
    if (template) {
      copy[index] = template;
      setTemplates(copy);
    }
  };

  const removeFollowUp = (index: number) => {
    const copy = [...templates];
    copy.splice(index, 1);
    setTemplates(copy);
  };

  const handleTimezoneChange = (_: CustomEvent, newValue: string | null) => {
    if (newValue) {
      setEmailSettings((prev) =>
        prev
          ? {
              ...prev,
              timezone: newValue,
            }
          : prev,
      );
      setTimezone(newValue);
    }
  };

  const handleDayToggle = (
    _: React.MouseEvent<HTMLElement>,
    newDays: number[],
  ) => {
    setSelectedDaysError("");
    setSelectedDays(newDays);
  };

  const onSubmit = (e: CustomEvent) => {
    e.preventDefault();
    goNext(false);
  };

  const goNext = async (overrideEmailLimit: boolean) => {
    let hasError = false;

    if (startDate && endDate && startDate.isAfter(endDate)) {
      setDateError("Your start date must be before your end date");
      hasError = true;
    }

    if (startTime && endTime) {
      const start = moment({
        hour: startTime.hour(),
        minute: startTime.minute(),
      });
      const end = moment({ hour: endTime.hour(), minute: endTime.minute() });

      if (start.isAfter(end)) {
        setTimeError("Your start time must be before your end time");
        hasError = true;
      }
    }

    // Selected days validation
    if (selectedDays.length === 0) {
      setSelectedDaysError(
        "Select at least one day in the week to send emails",
      );
      hasError = true;
    }

    if (hasError) {
      trackEvent("New Campaign Dialog Submitted Details", {
        "Has Front-end Error": true,
      });
      return;
    }

    if (campaign?.id) {
      handleApprovePendingCampaign(overrideEmailLimit);
      return;
    }

    setNextLoading(true);
    try {
      const res = await previewNewCampaign(true, overrideEmailLimit);

      const jsonMessage = await res.json();

      if (!res.ok) {
        trackEvent("New Campaign Dialog Submitted Details", {
          "Has Back-end Error": true,
          "Error Message": jsonMessage.message,
        });
        if (jsonMessage.field) {
          if (jsonMessage.field === "date") {
            setDateError(jsonMessage.message);
            return;
          }
          if (jsonMessage.field === "limitDate") {
            setDateLimitError(jsonMessage.message);
            return;
          }
          if (jsonMessage.field === "time") {
            setTimeError(jsonMessage.message);
            return;
          }
          if (
            jsonMessage.field === "templates" &&
            jsonMessage.templateIdx !== undefined &&
            jsonMessage.templateIdx !== null
          ) {
            setTemplatesError({
              idx: jsonMessage.templateIdx,
              message: jsonMessage.message,
            });
            return;
          }
          if (jsonMessage.field === "days_of_week") {
            setSelectedDaysError(jsonMessage.message);
            return;
          }

          if (jsonMessage.field === "paywall") {
            if (jsonMessage.message.includes("remaining this month")) {
              setUpgradeDialogSource("Hits Limit");
            } else if (jsonMessage.message.includes("emails at once")) {
              setUpgradeDialogSource("Hits Campaign Limit");
            } else {
              setUpgradeDialogSource("Campaign Limit");
            }
            return;
          }
        }

        setAlert(jsonMessage.message, "error");
      } else {
        setPreviewBrands(jsonMessage.previewedBentoBrands);
        setPreviewPage(2);
        setPreviewCount(jsonMessage.totalCount);
        navigate("../preview");
      }
    } catch (error) {
      setAlert(
        "Something went wrong, please contact hello@onbento.com for support or try again",
        "error",
      );
    } finally {
      setNextLoading(false);
    }
  };

  const handleApprovePendingCampaign = async (
    overrideEmailLimit: boolean = false,
  ) => {
    setNextLoading(true);
    const body = {
      templateIds: templates
        .map((t) => t.id)
        .filter((id): id is number => id !== undefined),
      daysOfWeek: selectedDays,
      timezone,
      startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
      endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
      startTime: startTime ? startTime.format("HH:mm:ss") : null,
      endTime: endTime ? endTime.format("HH:mm:ss") : null,
      excludedBentoBrandIds: Array.from(excludedBentoBrandIds),
      overrideEmailLimit,
    };
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/campaigns/${campaign?.id}/approve`,
        "POST",
        {},
        body,
      );
      setAlert("Successfully approved campaign.", "success");

      if (isLocal) {
        navigate(`../success`);
      } else {
        setCampaigns((prev) =>
          prev.map((c) => {
            if (c.id === campaign?.id) {
              return {
                ...c,
                status: CampaignStatus.in_progress,
              };
            }
            return c;
          }),
        );
        navigate(`/${routes.campaignView}`, { replace: true });
      }
    } catch (error) {
      if (error?.message?.includes("remaining this month")) {
        setUpgradeDialogSource("Hits Limit");
        return;
      } else if (error?.message?.includes("emails at once")) {
        setUpgradeDialogSource("Hits Campaign Limit");
        return;
      } else if (
        error?.message?.includes("above our recommended daily limit")
      ) {
        setDateLimitError(error?.message);
        return;
      }
      setErrorAlert(error);
    } finally {
      setNextLoading(false);
    }
  };

  const getRecommendedDate = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/campaigns/recommended-end-date`,
        "POST",
        {},
        {
          startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
          daysOfWeek: selectedDays,
          sequenceLength: templates.length,
          totalCount: totalSelectedBrands,
        },
      );
      setRecommendedEndTime(res.recommendedEndTime);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  useEffect(() => {
    if (startDate && selectedDays && totalSelectedBrands) {
      getRecommendedDate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, selectedDays, totalSelectedBrands]);

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-us">
        <form id="details-campaign-form" onSubmit={onSubmit}>
          <Grid container alignItems="flex-start" rowGap={1} sx={{ px: 2 }}>
            <Grid item xs={12}>
              {campaign ? (
                <Typography
                  variant="h6"
                  sx={{ fontSize: 15, color: grey[700] }}
                >
                  Campaign Details
                </Typography>
              ) : (
                <Typography variant="h6">Set Up Campaign</Typography>
              )}
            </Grid>

            {(!isLocal || templatesError) && (
              <>
                <Grid item xs={12} lg={3}>
                  <Typography sx={{ mt: 0.75 }}>Outreach Template</Typography>
                </Grid>
                <Grid item xs={12} lg={9}>
                  {templates[0] && (
                    <OutreachSelect
                      templates={templates}
                      handleChange={handleTemplateChange}
                      filteredOutreachTemplates={filteredOutreachTemplates}
                      templateFromEmailSettings={templateFromEmailSettings}
                    />
                  )}
                  {templatesError && templatesError.idx === 0 && (
                    <Grid container>
                      <Typography
                        variant="caption"
                        sx={{ fontStyle: "italic", color: "error.main" }}
                      >
                        {templatesError.message}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </>
            )}
            <Grid item xs={12} lg={3}>
              <Typography sx={{ mt: 0.75 }}>Sending Dates</Typography>
            </Grid>
            <Grid item xs={12} lg={9}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item>
                  <DatePicker
                    disablePast
                    value={startDate}
                    onChange={(value: Moment | null) => {
                      setStartDate(value);
                      if (endDate && value && value <= endDate) {
                        setDateError("");
                      }
                      setDisplayedEndDate(true);
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        required: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item>
                  <Typography>to</Typography>
                </Grid>
                <Grid item>
                  <DatePicker
                    disablePast
                    value={endDate}
                    onChange={(value: Moment | null) => {
                      setEndDate(value);
                      if (value && startDate && startDate <= value) {
                        setDateError("");
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        required: true,
                      },
                    }}
                  />
                </Grid>
                {recommendedEndTime?.length > 0 && displayEndDate && (
                  <FormHelperText
                    sx={{
                      ml: 1,
                      color: "primary.main",
                      fontSize: 10,
                      fontStyle: "italic",
                    }}
                  >
                    Recommended end date: <br />
                    {recommendedEndTime}
                  </FormHelperText>
                )}
              </Grid>
              {!!dateError && (
                <Grid container>
                  <Typography
                    variant="caption"
                    sx={{ fontStyle: "italic", color: "error.main" }}
                  >
                    {dateError}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12} lg={3}>
              <Typography sx={{ mt: 0.75 }}>Sending Time</Typography>
            </Grid>
            <Grid item xs={12} lg={9}>
              <Grid container alignItems="center" xs={12} spacing={1}>
                <Grid item>
                  <TimePicker
                    value={startTime}
                    onChange={(value: Moment | null) => {
                      setStartTime(value);
                      if (value && endTime && value <= endTime) {
                        setTimeError("");
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        required: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item>
                  <Typography>to</Typography>
                </Grid>
                <Grid item>
                  <TimePicker
                    value={endTime}
                    onChange={(value: Moment | null) => {
                      setEndTime(value);
                      if (value && startTime && startTime <= value) {
                        setTimeError("");
                      }
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        required: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item>
                  <Autocomplete
                    options={moment.tz.names()}
                    value={timezone}
                    onChange={handleTimezoneChange}
                    renderInput={(params) => <TextField {...params} required />}
                    disableClearable
                    size="small"
                    sx={{
                      minWidth: "222px",
                    }}
                  />
                </Grid>
              </Grid>
              {!!timeError && (
                <Grid container>
                  <Typography
                    variant="caption"
                    sx={{ fontStyle: "italic", color: "error.main" }}
                  >
                    {timeError}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12} lg={3}>
              <Typography sx={{ mt: 0.75 }}>Days of the Week</Typography>
            </Grid>
            <Grid item xs={12} lg={9}>
              <ToggleButtonGroup
                onChange={handleDayToggle}
                value={selectedDays}
                size="small"
              >
                {DAYS_OF_THE_WEEK.map((day) => (
                  <ToggleButton
                    key={day[0]}
                    value={day[0]}
                    disabled={day[0] === 5 || day[0] === 6} // disable the weekends
                  >
                    {day[1]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {!!selectedDaysError && (
                <Grid container>
                  <Typography
                    variant="caption"
                    sx={{ fontStyle: "italic", color: "error.main" }}
                  >
                    {selectedDaysError}
                  </Typography>
                </Grid>
              )}
            </Grid>
            {(!isLocal || templatesError) && (
              <>
                <Grid item xs={12} lg={3}>
                  <Typography
                    sx={{ mt: selectedFollowups.length > 0 ? 0.75 : 0.5 }}
                  >
                    Follow-up Templates
                  </Typography>
                </Grid>
                <Grid item xs={12} lg={9}>
                  {selectedFollowups?.map((followup, index) => (
                    <Grid container sx={{ mb: 1 }} key={index}>
                      <Grid container alignItems="center">
                        <Select
                          onChange={(e) => handleTemplateChange(e, index + 1)}
                          value={followup?.id}
                          size="small"
                        >
                          {filteredFollowUps?.map((template) => (
                            <MenuItem key={template.id} value={template.id}>
                              <Box>
                                {template.name}
                                {template.id &&
                                  templateFromEmailSettings?.includes(
                                    template.id,
                                  ) && (
                                    <Chip
                                      label="Last Used"
                                      size="small"
                                      sx={{
                                        ml: 1,
                                        fontSize: 10,
                                        backgroundColor: "primary.light",
                                      }}
                                    />
                                  )}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>

                        {followup?.id && (
                          <TemplateEditButton
                            sx={{ ml: 1 }}
                            templateId={followup.id}
                          />
                        )}

                        <IconButton
                          sx={{ fontSize: 13, color: grey[500] }}
                          onClick={() => removeFollowUp(index + 1)}
                        >
                          <Box component="i" className="fa-regular fa-trash" />
                        </IconButton>
                      </Grid>
                      {templatesError && templatesError.idx === index + 1 && (
                        <Grid container>
                          <Typography
                            variant="caption"
                            sx={{ fontStyle: "italic", color: "error.main" }}
                          >
                            {templatesError.message}
                          </Typography>
                        </Grid>
                      )}
                      <Grid container>
                        <Typography
                          variant="caption"
                          sx={{ fontStyle: "italic", fontSize: 10 }}
                          color="textSecondary"
                        >
                          Sent 4 business days after previous email
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                  {followupTemplates?.length > 0 &&
                    templates?.length < MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
                      <Grid container alignItems="center">
                        <Grid item xs md>
                          <Button
                            disableElevation
                            onClick={addFollowUp}
                            sx={{
                              fontSize: 12,
                              textTransform: "none",
                              px: 0,
                            }}
                          >
                            + Follow Up
                          </Button>
                        </Grid>
                      </Grid>
                    )}
                </Grid>
              </>
            )}
            {isAdmin && (
              <>
                <Grid item xs={12} sm={3}>
                  <Typography sx={{ mt: 0.75 }}>Requires Review</Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={status === CampaignStatus.pending_approval}
                        onChange={(e: CustomEvent) => {
                          const checked = e.target.checked;
                          if (checked) {
                            setStatus(CampaignStatus.pending_approval);
                          } else {
                            setStatus(CampaignStatus.in_progress);
                          }
                        }}
                      />
                    }
                    label="Requires User's Approval"
                  />

                  <FormHelperText>
                    If <strong>Requires User's Approval</strong> is selected,
                    the user will have a chance to approve the brands before the
                    campaign begins.
                  </FormHelperText>
                </Grid>
              </>
            )}
          </Grid>
        </form>
      </LocalizationProvider>
      <DialogActions sx={{ mt: 1 }}>
        {!campaign?.id && (
          <Button
            variant="text"
            onClick={() => {
              if (selectedCollection) {
                navigate("../collection");
              } else {
                navigate("../search");
              }
            }}
            disableElevation
          >
            Back
          </Button>
        )}
        {campaign?.id && (
          <Typography sx={{ fontSize: 12 }}>
            {totalSelectedBrands} brand{totalSelectedBrands > 1 ? "s" : ""} in
            this campaign
          </Typography>
        )}
        <Button
          disableElevation
          variant="contained"
          type="submit"
          form="details-campaign-form"
        >
          {campaign?.id ? <>Schedule Emails</> : "Next"}
        </Button>
      </DialogActions>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: 9999,
          position: "absolute",
          borderRadius: 2,
        }}
        open={nextLoading}
      >
        <CircularProgress />
      </Backdrop>
      <ConfirmDialog
        header="⚠️ Warning"
        subheader={<Markdown content={dateLimitError} />}
        open={!!dateLimitError}
        handleClose={() => setDateLimitError("")}
        handleConfirm={() => {
          setDateLimitError("");
        }}
        handleCancel={() => {
          goNext(true);
          setDateLimitError("");
        }}
        cancelText="Continue Anyway"
        confirmText="Update Dates"
      />
    </>
  );
}
